import React from 'react'

import { Chip, ChipProps } from '@mui/material'

// Shared removable value chip for multi-select Autocompletes (filters + field
// multi-selects). Soft rounded-rectangle (matches the design system's `rounded-md`
// Chip token and the status-filter chips), legible on the dark field: primary[100]
// surface + primary[200] hairline border, near-white text, generous left padding, and
// a delete icon that brightens on hover. Replaces the cramped bare grey MUI Chip that
// was hand-rolled in each renderTags. Spread getTagProps onto it as usual.
//
// ⚠️ `forwardRef`, and it is not decoration. Every MUI wrapper that needs to position or
// observe its child hands it a ref — `Tooltip` most commonly — and a plain function
// component cannot hold one: React logs "Function components cannot be given refs",
// the ref is dropped, and the wrapper silently degrades (a tooltip that never opens).
// This surfaced the first time a TagChip was wrapped in a `Tooltip` (the Analytics
// applied-filter chips, which show their hidden values on hover). Forwarding to the
// underlying `Chip` fixes it for every call site rather than making each one add a
// wrapper `<span>` — the same reasoning as `UnpaddedDateTimeRangeField`'s own forwardRef
// note in `AnalyticsFiltersHeader`.
export const TagChip = React.forwardRef<HTMLDivElement, ChipProps>(({ sx, ...props }, ref) => (
  <Chip
    ref={ref}
    size="small"
    {...props}
    sx={[
      (t) => ({
        height: 28,
        borderRadius: 1.5,
        backgroundColor: t.palette.primary[100],
        border: `1px solid ${t.palette.primary[200] ?? t.palette.primary[100]}`,
        color: t.palette.text.primary,
        fontSize: 13,
        fontWeight: 500,
        // Generous left breathing room; the delete icon supplies the right spacing.
        '& .MuiChip-label': { pl: 2.5, pr: 1 },
        '& .MuiChip-deleteIcon': {
          color: t.palette.text.secondary,
          fontSize: 16,
          ml: 0,
          mr: 1.5,
          '&:hover': { color: t.palette.text.primary },
        },
        // Avatar variant (e.g. MultiAutocompleteSelect) — keep it snug inside the chip.
        '& .MuiChip-avatar': { ml: 0.5 },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  />
))

TagChip.displayName = 'TagChip'
